/**
 * Auto Renewal API Client
 * Created: January 17, 2026
 * 
 * API hooks and functions for package auto renewal management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  UpdateAutoRenewalRequest,
  UpdateAutoRenewalResponse,
  GetAutoRenewalResponse,
  AutoRenewalPreviewRequest,
  AutoRenewalPreviewResponse,
  AutoRenewalHistory
} from '@/src/types/auto-renewal'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get current auto renewal settings
 */
export async function getAutoRenewalSettings(): Promise<GetAutoRenewalResponse> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/v1/auto-renewal/settings`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to get auto renewal settings')
  }
  
  return response.json()
}

/**
 * Update auto renewal settings
 */
export async function updateAutoRenewalSettings(
  settings: UpdateAutoRenewalRequest
): Promise<UpdateAutoRenewalResponse> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/v1/auto-renewal/settings`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(settings)
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to update auto renewal settings')
  }
  
  return response.json()
}

/**
 * Preview auto renewal cost and credit balance check
 */
export async function previewAutoRenewal(
  request: AutoRenewalPreviewRequest
): Promise<AutoRenewalPreviewResponse> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/v1/auto-renewal/preview`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(request)
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to preview auto renewal')
  }
  
  return response.json()
}

/**
 * Get auto renewal execution history
 */
export async function getAutoRenewalHistory(
  limit: number = 10,
  offset: number = 0
): Promise<AutoRenewalHistory> {
  const token = localStorage.getItem('authToken')
  
  const response = await fetch(
    `${API_BASE_URL}/api/v1/auto-renewal/history?limit=${limit}&offset=${offset}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  )
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to get auto renewal history')
  }
  
  return response.json()
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to get auto renewal settings
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useAutoRenewalSettings()
 * 
 * if (data?.data.auto_renewal_enabled) {
 *   // Show enabled state
 * }
 * ```
 */
export function useAutoRenewalSettings() {
  return useQuery({
    queryKey: ['auto-renewal-settings'],
    queryFn: getAutoRenewalSettings,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true
  })
}

/**
 * Hook to update auto renewal settings
 * 
 * @example
 * ```tsx
 * const { mutate: updateRenewal, isPending } = useUpdateAutoRenewal()
 * 
 * updateRenewal({
 *   enabled: true,
 *   method: 'credit_balance',
 *   settings: {
 *     notify_before_days: [7, 3, 1],
 *     notify_via: ['line_oa'],
 *     auto_renew_with_credits: true
 *   }
 * }, {
 *   onSuccess: () => {
 *     toast.success('บันทึกการตั้งค่าสำเร็จ')
 *   }
 * })
 * ```
 */
export function useUpdateAutoRenewal() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (settings: UpdateAutoRenewalRequest) => 
      updateAutoRenewalSettings(settings),
    onSuccess: () => {
      // Invalidate auto renewal settings query to refetch
      queryClient.invalidateQueries({ queryKey: ['auto-renewal-settings'] })
      // Also invalidate package status as it may affect renewal
      queryClient.invalidateQueries({ queryKey: ['customer-package-status'] })
    },
    onError: (error: Error) => {
      console.error('Failed to update auto renewal settings:', error)
    }
  })
}

/**
 * Hook to preview auto renewal
 * 
 * @example
 * ```tsx
 * const { data: preview, isLoading } = useAutoRenewalPreview('credit_balance')
 * 
 * if (preview?.data.credit_balance.is_sufficient) {
 *   // Show success state
 * } else {
 *   // Show insufficient balance warning
 * }
 * ```
 */
export function useAutoRenewalPreview(
  method: AutoRenewalPreviewRequest['method'],
  packageId?: string
) {
  return useQuery({
    queryKey: ['auto-renewal-preview', method, packageId],
    queryFn: () => previewAutoRenewal({ method, packageId }),
    staleTime: 10000, // 10 seconds
    enabled: !!method
  })
}

/**
 * Hook to get auto renewal history
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useAutoRenewalHistory(10, 0)
 * 
 * data?.executions.map(exec => (
 *   <div key={exec.id}>
 *     {exec.status === 'completed' ? '✓' : '✗'} {exec.package_name}
 *   </div>
 * ))
 * ```
 */
export function useAutoRenewalHistory(
  limit: number = 10,
  offset: number = 0
) {
  return useQuery({
    queryKey: ['auto-renewal-history', limit, offset],
    queryFn: () => getAutoRenewalHistory(limit, offset),
    staleTime: 30000 // 30 seconds
  })
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook that combines auto renewal settings with preview
 * Useful for auto renewal toggle UI
 * 
 * @example
 * ```tsx
 * const renewalInfo = useAutoRenewalInfo()
 * 
 * if (renewalInfo.enabled && !renewalInfo.canAutoRenew) {
 *   // Show warning: insufficient balance
 * }
 * ```
 */
export function useAutoRenewalInfo() {
  const { data: settings } = useAutoRenewalSettings()
  const { data: preview } = useAutoRenewalPreview(
    settings?.data.auto_renewal_method ?? 'credit_balance'
  )
  
  return {
    enabled: settings?.data.auto_renewal_enabled ?? false,
    method: settings?.data.auto_renewal_method ?? null,
    preferences: settings?.data.auto_renewal_settings ?? null,
    canAutoRenew: preview?.data.can_auto_renew ?? false,
    isSufficient: preview?.data.credit_balance?.is_sufficient ?? false,
    currentBalance: preview?.data.credit_balance?.current ?? 0,
    requiredAmount: preview?.data.credit_balance?.required ?? 0,
    packagePrice: preview?.data.cost?.amount ?? 0,
    estimatedRenewalDate: preview?.data.estimated_renewal_date ?? null
  }
}

/**
 * Hook for toggling auto renewal on/off
 * Simplified interface for toggle switches
 * 
 * @example
 * ```tsx
 * const { toggle, isEnabled, isPending } = useAutoRenewalToggle()
 * 
 * <Switch 
 *   checked={isEnabled} 
 *   onCheckedChange={toggle}
 *   disabled={isPending}
 * />
 * ```
 */
export function useAutoRenewalToggle() {
  const { data: settings } = useAutoRenewalSettings()
  const { mutate: update, isPending } = useUpdateAutoRenewal()
  
  const isEnabled = settings?.data.auto_renewal_enabled ?? false
  
  const toggle = (enabled: boolean) => {
    const currentSettings = settings?.data.auto_renewal_settings
    const currentMethod = settings?.data.auto_renewal_method ?? 'credit_balance'
    
    update({
      enabled,
      method: currentMethod,
      settings: currentSettings ?? {
        notify_before_days: [7, 3, 1],
        notify_via: ['line_oa'],
        auto_renew_with_credits: true
      }
    })
  }
  
  return {
    isEnabled,
    toggle,
    isPending
  }
}

/**
 * Hook for calculating days until renewal
 * 
 * @example
 * ```tsx
 * const { daysUntilRenewal, isApproaching } = useDaysUntilRenewal()
 * 
 * if (isApproaching) {
 *   // Show renewal reminder
 * }
 * ```
 */
export function useDaysUntilRenewal() {
  const { data: settings } = useAutoRenewalSettings()
  
  const expiresAt = settings?.data.current_package?.expires_at
  const daysRemaining = settings?.data.current_package?.days_remaining ?? null
  
  const isApproaching = daysRemaining !== null && daysRemaining <= 7
  const isUrgent = daysRemaining !== null && daysRemaining <= 3
  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 1
  
  return {
    daysUntilRenewal: daysRemaining,
    isApproaching,
    isUrgent,
    isExpiringSoon,
    expiresAt
  }
}

/**
 * Hook for validating auto renewal method availability
 * 
 * @example
 * ```tsx
 * const validation = useAutoRenewalMethodValidation('credit_balance')
 * 
 * if (!validation.isAvailable) {
 *   // Show warning: {validation.reason}
 * }
 * ```
 */
export function useAutoRenewalMethodValidation(
  method: UpdateAutoRenewalRequest['method']
) {
  const { data: preview } = useAutoRenewalPreview(method)
  
  const isAvailable = method === 'qr_code' 
    ? true 
    : preview?.data.credit_balance?.is_sufficient ?? false
  
  const reason = !isAvailable && method === 'credit_balance'
    ? 'ยอดเครดิตคงเหลือไม่เพียงพอสำหรับการต่ออายุอัตโนมัติ'
    : null
  
  return {
    method,
    isAvailable,
    reason,
    requiredBalance: preview?.data.credit_balance?.required ?? 0,
    currentBalance: preview?.data.credit_balance?.current ?? 0,
    shortfall: preview?.data.credit_balance
      ? Math.max(0, preview.data.credit_balance.required - preview.data.credit_balance.current)
      : 0
  }
}
